import React, { useState, useEffect } from 'react';
import jwilUpdatesService from '../services/jwilUpdatesService';
import { JwilUpdate } from '../types/contentManagement';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const JwilUpdatesManagement: React.FC = () => {
  const [updates, setUpdates] = useState<JwilUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<Partial<JwilUpdate> | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateToDelete, setUpdateToDelete] = useState<JwilUpdate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Helper function to convert dd-mm-yyyy to yyyy-mm-dd for input field
  const convertToInputFormat = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    // If already in yyyy-mm-dd format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert dd-mm-yyyy to yyyy-mm-dd
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const data = await jwilUpdatesService.getAllUpdates(true);
      // Sort by title in ascending order
      const sortedData = (data || []).sort((a, b) => 
        a.title.localeCompare(b.title)
      );
      setUpdates(sortedData);
      setError(null);
      setImageRefreshKey(Date.now()); // Update refresh key when data is fetched
    } catch (err) {
      setError('Failed to fetch JWIL updates');
      console.error('Error fetching updates:', err);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingUpdate({
      title: '',
      description: '',
      link: '',
      date: new Date().toISOString().split('T')[0],
      is_visible: true,
      sort_order: updates.length + 1
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (update: JwilUpdate) => {
    setEditingUpdate({ 
      ...update,
      date: convertToInputFormat(update.date) // Convert date for input field
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUpdate(null);
    setSelectedFile(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setSaveMessage('Error: Please select an image file');
        setTimeout(() => setSaveMessage(''), 3000);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setSaveMessage('Error: Image size must be less than 25MB');
        setTimeout(() => setSaveMessage(''), 3000);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    if (!editingUpdate || !editingUpdate.title || !editingUpdate.description) {
      setSaveMessage('Error: Title and Description are required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append('title', editingUpdate.title);
      formData.append('description', editingUpdate.description);
      if (editingUpdate.link) formData.append('link', editingUpdate.link);
      formData.append('date', editingUpdate.date || new Date().toISOString().split('T')[0]);
      formData.append('is_visible', String(editingUpdate.is_visible ?? true));
      formData.append('sort_order', String(editingUpdate.sort_order || updates.length + 1));
      
      if (selectedFile) {
        formData.append('image', selectedFile);
      }

      if (editingUpdate.id) {
        await jwilUpdatesService.updateUpdate(editingUpdate.id, formData);
        setSaveMessage('Update saved successfully');
      } else {
        await jwilUpdatesService.createUpdate(formData);
        setSaveMessage('Update created successfully');
      }
      
      await fetchUpdates();
      handleCloseModal();
      setCurrentPage(1);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error saving update');
      console.error('Error saving update:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (update: JwilUpdate) => {
    setUpdateToDelete(update);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!updateToDelete) return;

    try {
      setSubmitting(true);
      await jwilUpdatesService.deleteUpdate(updateToDelete.id);
      await fetchUpdates();
      setCurrentPage(1);
      setSaveMessage('Update deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error deleting update');
      console.error('Error deleting update:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
      setUpdateToDelete(null);
    }
  };

  const handleToggleVisibility = async (id: number) => {
    try {
      await jwilUpdatesService.toggleVisibility(id);
      await fetchUpdates();
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  // Filter updates based on search query
  const filteredUpdates = updates.filter(update => {
    const query = searchQuery.toLowerCase();
    return update.title.toLowerCase().includes(query);
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUpdates = filteredUpdates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUpdates.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading JWIL updates...</div>;
  }

  return (
    <div className="edit-section">
      <div className="applications-list">
        <div className="list-header">
          <h4>JWIL Updates Management</h4>
          <button 
            className="btn-add" 
            onClick={handleAddClick}
            disabled={submitting}
          >
            <i className="icon-plus"></i>
            Add Update
          </button>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
            border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
            position: 'relative'
          }}>
            {saveMessage}
            <button
              onClick={() => setSaveMessage('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '8px',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close message"
            >
              ×
            </button>
          </div>
        )}

        {error && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}

        {/* Search Bar */}
        <div style={{ marginTop: '20px', marginBottom: '15px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                paddingLeft: '35px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                width: '100%',
                padding: '8px 8px 8px 35px'
              }}
            />
            <i 
              className="fas fa-search" 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d'
              }}
            ></i>
          </div>
          {searchQuery && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
              Found {filteredUpdates.length} update{filteredUpdates.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Image</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '20%' }}>Title</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '30%' }}>Description</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Date</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Visible</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Order</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUpdates.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                    {searchQuery ? 'No updates found matching your search' : 'No updates found. Click "Add Update" to create one.'}
                  </td>
                </tr>
              ) : (
                currentUpdates.map((update) => (
                  <tr 
                    key={update.id}
                    style={{ 
                      borderBottom: '1px solid #ddd'
                    }}
                  >
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <img 
                        src={update.image ? `${jwilUpdatesService.getImageUrl(update.image)}?t=${imageRefreshKey}` : '/assets/images/logo.png'} 
                        alt={update.title}
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/assets/images/logo.png';
                        }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>{update.title}</strong>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>{update.description.substring(0, 100)}{update.description.length > 100 ? '...' : ''}</span>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {update.date}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={update.is_visible}
                        onChange={() => handleToggleVisibility(update.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {update.sort_order}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleEditClick(update)}
                          disabled={submitting}
                          title="Edit update"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#e3f2fd')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          ✏️
                        </button>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleDeleteClick(update)}
                          disabled={submitting}
                          title="Delete update"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#ffebee')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredUpdates.length > itemsPerPage && (
            <div style={{ 
              marginTop: '20px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '10px 0'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUpdates.length)} of {filteredUpdates.length} updates
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#007bff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: currentPage === page ? '#007bff' : 'white',
                      color: currentPage === page ? 'white' : '#007bff',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: currentPage === page ? 'bold' : 'normal'
                    }}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                    color: currentPage === totalPages ? '#6c757d' : '#007bff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingUpdate?.id ? 'Edit JWIL Update' : 'Add New JWIL Update'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Title <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={editingUpdate?.title || ''}
              onChange={(e) => setEditingUpdate(prev => ({ ...prev, title: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="Enter title"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Description <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={editingUpdate?.description || ''}
              onChange={(e) => setEditingUpdate(prev => ({ ...prev, description: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
              placeholder="Enter description"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Link (Optional)</label>
            <input
              type="url"
              value={editingUpdate?.link || ''}
              onChange={(e) => setEditingUpdate(prev => ({ ...prev, link: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Date</label>
            <input
              type="date"
              value={editingUpdate?.date || ''}
              onChange={(e) => setEditingUpdate(prev => ({ ...prev, date: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Image</label>
            <input
              id="jwilUpdateImageInput"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Recommended size: 350×200 pixels (16:9 ratio). Max 5MB.
            </p>
            {editingUpdate?.image && !selectedFile && (
              <div style={{ marginTop: '8px' }}>
                <img 
                  src={`${jwilUpdatesService.getImageUrl(editingUpdate.image)}?t=${Date.now()}`}
                  alt="Current"
                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                  onError={(e) => {
                    console.error('Failed to load image:', editingUpdate.image);
                    (e.target as HTMLImageElement).src = '/assets/images/logo.png';
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Current image</p>
              </div>
            )}
            {selectedFile && (
              <div style={{ marginTop: '8px' }}>
                <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>New Image (Cropped):</p>
                <img 
                  src={URL.createObjectURL(selectedFile)}
                  alt="Cropped preview"
                  style={{ width: '200px', height: 'auto', objectFit: 'cover', borderRadius: '4px', border: '2px solid #00a0e3' }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="is_visible"
              checked={editingUpdate?.is_visible ?? true}
              onChange={(e) => setEditingUpdate(prev => ({ ...prev, is_visible: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="is_visible" style={{ cursor: 'pointer', fontWeight: '500' }}>Visible on homepage</label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <button
              onClick={handleCloseModal}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete JWIL Update"
        message="Are you sure you want to delete this update?"
        itemName={updateToDelete?.title}
      />
    </div>
  );
};

export default JwilUpdatesManagement;
